import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ConfigService } from '../../configuration/services/config.service';
import { IUserProfileEntity } from '../../entities/authentication/authentication.interfaces';
import { LoggingService } from '../../logging/logging.service';
import { BaseService, IBaseService } from '../../services/misc/base.service';

export enum AuthenticationStatus {

    LoggedOut = 1,
    InLoginCheckUser = 2,
    InLoginUserChecked = 4,
    InLoginCheckPassword = 8,
    InLoginPasswordChecked = 16,
    WaitingFor2WayAuthentication = 32,
    LoggedIn = 64,
    InLogout = 128,
    InRegister = 256

}

export enum LoginType {

    Biometric = 1,
    Credentials = 2,
    UserName = 4,
    EasyEntry = 8,
    PasswordReset = 16

}

export interface IAppProfile {

    ip?: string;
    userProfile?: IUserProfileEntity;

}

export interface IAuthenticationService extends IBaseService {

    authenticationStatus: AuthenticationStatus;

    initializeAsync(): Promise<void>;
    loginUserNameAsync(mandator: string, userName: string, params?: any): Promise<any>;
    loginAsync(loginType: LoginType, mandator: string, username: string, password: string, params?: any): Promise<any>;
    logoutAsync(userId: any): Promise<any>;
    registerAsync(appProfile: IAppProfile): Promise<any>;
    resetPasswordAsync(mandator: string, userName: string): Promise<any>;
    subscribeForAuthenticationStatusChange(method: (status: AuthenticationStatus) => void): Subscription;

}

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService extends BaseService implements IAuthenticationService {

    private _authenticationStatus$: BehaviorSubject<AuthenticationStatus> = new BehaviorSubject<AuthenticationStatus>(AuthenticationStatus.LoggedOut);

    constructor(configurationService: ConfigService, loggingService: LoggingService) {
        super(configurationService, loggingService);

        this.loggingService.debug('AuthenticationService --> constructor()');
    }

    public get authenticationStatus(): AuthenticationStatus {
        return this.authenticationStatus$.getValue();
    }

    public set authenticationStatus(value: AuthenticationStatus) {
        this.doSetAuthenticationStatus(value);
    }

    public async loginUserNameAsync(mandator: string, userName: string, params?: any): Promise<any> {
        return (await this.doLoginUserNameAsync(mandator, userName, params));
    }

    public async loginAsync(loginType: LoginType, mandator: string, userName: string, password: string, params?: any): Promise<any> {
        return (await this.doLoginAsync(loginType, mandator, userName, password, params));
    }

    public async logoutAsync(userId: any): Promise<any> {
        return (await this.doLogoutAsync(userId));
    }

    public async sendVerificationCodeAsync(code: string): Promise<any> {
        return (await this.doSendVerificationCodeAsync(code));
    }

    public async registerAsync(appProfile: IAppProfile): Promise<any> {
        return (await this.doRegisterAsync(appProfile));
    }

    public async resetPasswordAsync(mandator: string, username: string): Promise<any> {
        return (await this.doResetPasswordAsync(mandator, username));
    }

    public subscribeForAuthenticationStatusChange(method: (status: AuthenticationStatus) => void): Subscription {
        return this.authenticationStatus$.subscribe(status => (method(status)));
    }

    protected get authenticationStatus$(): BehaviorSubject<AuthenticationStatus> {
        return this._authenticationStatus$;
    }

    protected override async doInitializeAsync(): Promise<void> {
    }

    protected async doLoginUserNameAsync(mandator: string, userName: string, params?: any): Promise<any> {
        return null;
    }

    protected async doLoginAsync(loginType: LoginType, mandator: string, userName: string, password: string, params?: any): Promise<any> {
        return null;
    }

    protected async doLogoutAsync(userId: any): Promise<any> {
        return null;
    }

    protected async doRegisterAsync(appProfile: IAppProfile): Promise<any> {
        return null;
    }

    protected async doResetPasswordAsync(mandator: string, username: string): Promise<any> {
        return null;
    }

    protected async doSendVerificationCodeAsync(code: string): Promise<any> {
    }

    protected doSetAuthenticationStatus(value: AuthenticationStatus): void {
        if (value !== this.authenticationStatus) {
            this.authenticationStatus$.next(value);
        }
    }

}
